import { GoogleGenAI } from "@google/genai";

// Get API key from environment - required
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

if (!GEMINI_API_KEY) {
  console.error('REACT_APP_GEMINI_API_KEY environment variable is required');
}

// Always create a fresh client
const getAIClient = () => new GoogleGenAI({ apiKey: GEMINI_API_KEY });

const getPersonaContext = (profile) => {
  const type = profile.type || 'business';
  return type === 'business' 
    ? `You are an elite Corporate Brand Strategist managing a commercial business account.
       CRITICAL RULES FOR BUSINESS MODE:
       1. NEVER use "I", "me", or "my". Always use collective branding ("We", "Our") or the business name.
       2. Focus on product/service benefits, company news, industry insights, and professional value.
       3. Tone: Professional, authoritative, trustworthy, and commercially driven.
       4. Goal: Drive traffic, leads, and sales for the business.`
    : `You are a charismatic Personal Brand Influencer and Storyteller.
       CRITICAL RULES FOR PERSONAL MODE:
       1. ALWAYS use "I", "me", and "my". Speak from a personal perspective.
       2. Focus on personal stories, opinions, daily life, and emotional connection.
       3. Tone: Authentic, vulnerable, conversational, and relatable.
       4. Goal: Build a loyal community and personal connection.`;
};

// Platform-specific best practices for maximum engagement
const getPlatformBestPractices = () => `
PLATFORM-SPECIFIC ENGAGEMENT BEST PRACTICES (MUST FOLLOW):

📸 INSTAGRAM:
- Lead with a scroll-stopping hook in the first line
- Use line breaks for readability (no walls of text)
- 3-5 relevant hashtags in comments or end of caption, NOT cluttering the main content
- Ask a question or include a CTA to drive comments
- Optimal length: 125-150 characters for feed, longer for carousels
- Use emojis strategically to break up text and add personality
- Focus on visual storytelling - describe the image connection

🐦 TWITTER/X:
- Hook in first 5 words - be punchy and provocative
- Optimal length: 70-100 characters for maximum engagement
- Use threads for longer content, but make tweet 1 standalone valuable
- 1-2 hashtags MAX, integrated naturally
- Ask controversial questions or share hot takes
- Use numbers and lists (they perform 2x better)
- End with a clear CTA or question

💼 LINKEDIN:
- Open with a personal story or surprising stat
- Use "I" statements even for business - LinkedIn rewards authenticity
- Line breaks after every 1-2 sentences
- Optimal length: 1,200-1,500 characters (short posts get 2x reach)
- NO hashtags in the main content - add 3-5 at the very end
- Include a thought-provoking question to drive comments
- Professional but human tone - vulnerability works well

📘 FACEBOOK:
- Lead with emotion or curiosity gap
- Optimal length: 40-80 characters for highest engagement
- Questions get 100% more comments
- Use storytelling format - beginning, middle, end
- Native video mentions perform better
- 1-2 hashtags only, if any
- Include a clear call-to-action

🎵 TIKTOK:
- Reference the visual/video hook in the caption
- Keep captions SHORT: 20-50 characters ideal
- Use trending sounds/formats when relevant
- 3-5 niche hashtags + 1-2 trending
- Conversational, Gen-Z friendly tone
- Use captions that create curiosity
- No formal language - be authentic and casual

🦋 BLUESKY:
- Similar to early Twitter - focus on witty, conversational content
- 300 character limit - be concise but complete
- Thread longer thoughts with clear numbering
- Minimal hashtags - community prefers organic discovery
- Engage with reply culture - ask for opinions
- Tech-savvy, thoughtful audience - avoid corporate speak
- Authentic voice over polished marketing

GENERAL ENGAGEMENT RULES:
- Each platform should feel NATIVE, not cross-posted
- Adapt tone, length, and format for each platform
- Focus on providing VALUE in every post
- Create content people want to SHARE
- Time-sensitive hooks increase urgency
- Use power words: Free, New, Secret, Limited, Exclusive, Proven
`;

export const generateContentCalendar = async (profile) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Call backend API for content calendar generation
  const response = await fetch(`${BACKEND_URL}/api/generate-calendar`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      business_name: profile.name,
      business_description: profile.description,
      website: profile.website || null,
      platforms: profile.platforms,
      days: profile.generationDays || 7,
      posts_per_day: profile.postsPerDay || 1,
      instructions: profile.instructions || null,
      news_topics: profile.newsTopics || [],
      use_trending_news: profile.useTrendingNews || false,
      profile_type: profile.type || 'business'
    }),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('Backend API error:', response.status, errorText);
    throw new Error(`Server error: ${response.status}`);
  }
  
  const data = await response.json();
  
  if (data.success && data.data) {
    return data.data;
  }
  
  // Backend returned an error
  console.error('Backend generation failed:', data.error);
  throw new Error(data.error || 'Failed to generate content');
};

// Client-side fallback using Google SDK directly
const generateContentCalendarClientSide = async (profile) => {
  const ai = getAIClient();
  const platformsStr = profile.platforms.join(", ");
  const topicsStr = profile.newsTopics?.join(", ") || "General industry trends";
  const days = profile.generationDays || 7;
  const frequency = profile.postsPerDay || 1;
  const useSearch = profile.useTrendingNews;
  
  const totalPosts = days * profile.platforms.length * frequency;
  const personaContext = getPersonaContext(profile);
  const platformBestPractices = getPlatformBestPractices();
  const profileType = (profile.type || 'business').toUpperCase();

  const today = new Date().toISOString().split('T')[0];

  const prompt = `
    ${personaContext}
    
    ${platformBestPractices}
    
    CONTEXT:
    Profile Type: ${profileType}
    Name: ${profile.name}
    Description: ${profile.description}
    ${profile.website ? `URL: ${profile.website}` : ''}
    Target Platforms: ${platformsStr}
    
    VOLUME REQUIREMENTS (STRICT):
    - Duration: Generate content for exactly ${days} days starting from today (${today}).
    - Frequency: Generate exactly ${frequency} post(s) PER platform PER day.
    - Platforms Count: ${profile.platforms.length}
    - TOTAL POSTS TO GENERATE: ${totalPosts}
    
    CRITICAL: Each post MUST follow the platform-specific best practices listed above. 
    Content should feel NATIVE to each platform, not like cross-posted generic content.
    Optimize for maximum engagement on each specific platform.
    
    SPECIAL MISSION: RESEARCH TRENDING NEWS
    Topics to Research: ${topicsStr}
    ${useSearch ? "Perform a Google Search to find real-time news articles from the last 24-48 hours. Base content on ACTUAL current events and news." : "Use your internal knowledge bases."}
    
    ${profile.instructions ? `SPECIAL INSTRUCTIONS: ${profile.instructions}
    
    URL HANDLING: If the instructions contain any URLs (http:// or https://), you MUST:
    1. Visit and read the content at that URL using Google Search
    2. Extract key information, quotes, statistics, and main points
    3. Generate social media posts that promote, discuss, or share insights from that content
    4. Reference the URL appropriately for each platform (link in bio for Instagram, direct link for Twitter/LinkedIn, etc.)
    5. Create compelling hooks that make people want to click and read the full content
    ` : ''}
    
    OUTPUT REQUIREMENT:
    - Respond strictly in VALID JSON format.
    - The JSON object must contain 'newsSummary' and 'posts'.
    - Each post must have: date, time, platform, content, hashtags, imagePrompt, engagementScore, engagementReasoning
    - The engagementReasoning should reference platform-specific best practices being applied
  `;

  const schema = {
    type: "OBJECT",
    properties: {
      newsSummary: { type: "STRING" },
      posts: {
        type: "ARRAY",
        items: {
          type: "OBJECT",
          properties: {
            date: { type: "STRING" },
            time: { type: "STRING" },
            platform: { type: "STRING" },
            content: { type: "STRING" },
            hashtags: { type: "ARRAY", items: { type: "STRING" } },
            imagePrompt: { type: "STRING" },
            engagementScore: { type: "NUMBER" },
            engagementReasoning: { type: "STRING" },
          },
          required: ["date", "time", "platform", "content", "hashtags", "imagePrompt", "engagementScore", "engagementReasoning"],
        },
      },
    },
  };

  try {
    // Use gemini-2.5-flash for all content generation
    const modelName = "gemini-2.5-flash";
    
    const response = await ai.models.generateContent({
      model: modelName,
      contents: prompt,
      config: {
        tools: useSearch ? [{ googleSearch: {} }] : undefined,
        // Only use JSON mode when not using search (grounding may add extra text)
        responseMimeType: useSearch ? undefined : "application/json",
        responseSchema: useSearch ? undefined : schema,
      },
    });

    const candidate = response.candidates?.[0];
    const groundingMetadata = candidate?.groundingMetadata;

    if (response.text) {
      let jsonStr = response.text.trim();
      
      // Clean up markdown code blocks
      if (jsonStr.includes("```")) {
        jsonStr = jsonStr.replace(/```json\s*/g, "").replace(/```\s*/g, "");
      }
      
      // Extract JSON object - find matching braces
      const firstBrace = jsonStr.indexOf('{');
      if (firstBrace !== -1) {
        let braceCount = 0;
        let lastBrace = -1;
        for (let i = firstBrace; i < jsonStr.length; i++) {
          if (jsonStr[i] === '{') braceCount++;
          if (jsonStr[i] === '}') {
            braceCount--;
            if (braceCount === 0) {
              lastBrace = i;
              break;
            }
          }
        }
        if (lastBrace !== -1) {
          jsonStr = jsonStr.substring(firstBrace, lastBrace + 1);
        }
      }

      let parsed = JSON.parse(jsonStr);

      // Extract real sources from grounding metadata (groundingChunks array)
      const groundingMetadata = candidate?.groundingMetadata;
      
      let extractedSources = [];
      
      // Extract sources from groundingChunks (contains actual URLs)
      if (groundingMetadata?.groundingChunks) {
        extractedSources = groundingMetadata.groundingChunks
          .filter(chunk => chunk.web)
          .map(chunk => ({ 
            title: chunk.web.title || 'News Article', 
            url: chunk.web.uri 
          }));
      }
      
      // Deduplicate sources by URL
      const uniqueSources = extractedSources.reduce((acc, source) => {
        if (source.url && !acc.find(s => s.url === source.url)) {
          acc.push(source);
        }
        return acc;
      }, []);

      // Add sources to each post if we have grounding data
      if (parsed.posts && uniqueSources.length > 0) {
        parsed.posts = parsed.posts.map(post => ({
          ...post,
          sources: uniqueSources.slice(0, 5) // Limit to 5 sources per post
        }));
      }

      return parsed;
    }
    throw new Error("No response text generated");
  } catch (error) {
    console.error("Error generating calendar:", error);
    throw error;
  }
};

export const regenerateSinglePost = async (currentPost, instruction, profile) => {
  const ai = getAIClient();
  const personaContext = getPersonaContext(profile);

  const prompt = `
    ${personaContext}

    TASK: REWRITE A SPECIFIC POST BASED ON USER INSTRUCTIONS.
    
    CONTEXT:
    Platform: ${currentPost.platform}
    Date: ${currentPost.date}
    Business/Name: ${profile.name}
    
    CURRENT CONTENT:
    "${currentPost.content}"
    
    USER INSTRUCTION (What to change):
    "${instruction}"
    
    REQUIREMENTS:
    1. Rewrite the content to strictly follow the user's instruction.
    2. Maintain the correct persona (${profile.type || 'business'}).
    3. Update hashtags if necessary.
    4. Update the image prompt if the content change requires a new visual direction.
    5. Re-evaluate the engagement score based on the new version.

    OUTPUT:
    Respond in VALID JSON with the following structure:
    {
      "content": "new text content...",
      "hashtags": ["tag1", "tag2"],
      "imagePrompt": "new image prompt...",
      "engagementScore": 95,
      "engagementReasoning": "Why this new version works better..."
    }
  `;

  const schema = {
    type: "OBJECT",
    properties: {
      content: { type: "STRING" },
      hashtags: { type: "ARRAY", items: { type: "STRING" } },
      imagePrompt: { type: "STRING" },
      engagementScore: { type: "NUMBER" },
      engagementReasoning: { type: "STRING" },
    },
    required: ["content", "hashtags", "imagePrompt", "engagementScore", "engagementReasoning"],
  };

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: schema,
      },
    });

    if (response.text) {
      return JSON.parse(response.text);
    }
    throw new Error("No response generated");
  } catch (error) {
    console.error("Error regenerating post:", error);
    throw error;
  }
};

export const generatePostImage = async (imagePrompt) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  try {
    // Call backend API for image generation (uses Gemini Nano Banana)
    const response = await fetch(`${BACKEND_URL}/api/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt: imagePrompt }),
    });
    
    const data = await response.json();
    
    if (data.success && data.image_url) {
      return data.image_url;
    }
    
    // Fallback: if no image generated, create a placeholder
    console.log("Image generation failed, creating placeholder:", data.error);
    return createPlaceholderImage(imagePrompt);
  } catch (error) {
    console.error("Error generating image:", error);
    // Fallback to placeholder on error
    return createPlaceholderImage(imagePrompt);
  }
};

// Fallback placeholder image generator
const createPlaceholderImage = (imagePrompt) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext('2d');
  
  // Create gradient background
  const gradient = ctx.createLinearGradient(0, 0, 1080, 1080);
  gradient.addColorStop(0, '#6366f1');
  gradient.addColorStop(0.5, '#8b5cf6');
  gradient.addColorStop(1, '#a855f7');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1080, 1080);
  
  // Add pattern overlay
  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  for (let i = 0; i < 20; i++) {
    const x = Math.random() * 1080;
    const y = Math.random() * 1080;
    const size = Math.random() * 100 + 50;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
  }
  
  // Add text
  ctx.fillStyle = 'white';
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('AI Generated Visual', 540, 500);
  
  ctx.font = '24px Arial';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  const words = imagePrompt.split(' ').slice(0, 10).join(' ');
  ctx.fillText(words + '...', 540, 560);
  
  return canvas.toDataURL('image/png');
};
